import { useIntl } from 'react-intl'
import styled from 'styled-components'
import CustomTooltip from 'components/ui/CustomTooltip'
import { HoleToolIcon } from 'assets/icons/hole-tool'
import { GreenToolIcon } from 'assets/icons/green-tool'
import { EditToolIcon } from 'assets/icons/edit-tool'
import { HazardToolIcon } from 'assets/icons/hazard-tool'
import { UndoIcon } from 'assets/icons/undo'
import { RedoIcon } from 'assets/icons/redo'
import { IconButton } from '@mui/material'
import { IconDivider } from './CourseGpsEditorElements.styles'

const isMacPlatform = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform)
const modifierKeyLabel = isMacPlatform ? '⌘' : 'Ctrl+'
const undoShortcut = `${modifierKeyLabel}Z`
const redoShortcut = isMacPlatform ? '⇧⌘Z' : 'Ctrl+Shift+Z'

const tooltipWithShortcut = (label: string, shortcut: string): JSX.Element => (
  <TooltipLine>
    <span>{label}</span>
    <ShortcutLabel>{shortcut}</ShortcutLabel>
  </TooltipLine>
)

interface Props {
  undoDisabled: boolean
  redoDisabled: boolean
  holeTool: boolean
  greenMapping: boolean
  onSelectEditTool: () => void
  onUndo: () => void
  onRedo: () => void
  onToggleHoleTool: () => void
  onToggleGreenMapping: () => void
}

export default function CourseGpsEditorToolbar({
  undoDisabled,
  redoDisabled,
  holeTool,
  greenMapping,
  onSelectEditTool,
  onUndo,
  onRedo,
  onToggleHoleTool,
  onToggleGreenMapping,
}: Props): JSX.Element {
  const intl = useIntl()
  const editToolSelected = !holeTool && !greenMapping

  return (
    <Toolbar>
      <CustomTooltip title={tooltipWithShortcut('Edit', 'E')} enterDelay={500}>
        <ToggleToolButton $active={editToolSelected} onClick={onSelectEditTool}>
          <EditToolIcon />
        </ToggleToolButton>
      </CustomTooltip>
      <CustomTooltip title={tooltipWithShortcut('Hole', 'H')} enterDelay={500}>
        <ToggleToolButton $active={holeTool} onClick={onToggleHoleTool}>
          <HoleToolIcon />
        </ToggleToolButton>
      </CustomTooltip>
      <CustomTooltip title={'Hazard'} enterDelay={500}>
        <ToggleToolButton $active={false} disabled>
          <HazardToolIcon />
        </ToggleToolButton>
      </CustomTooltip>
      <CustomTooltip title={tooltipWithShortcut('Green', 'G')} enterDelay={500}>
        <ToggleToolButton $active={greenMapping} onClick={onToggleGreenMapping}>
          <GreenToolIcon />
        </ToggleToolButton>
      </CustomTooltip>
      <IconDivider />

      <UndoRedoContainer>
        <CustomTooltip
          title={tooltipWithShortcut(intl.formatMessage({ id: 'strings.undo' }), undoShortcut)}
          enterDelay={500}
        >
          <UndoRedoButton onClick={onUndo} disabled={undoDisabled}>
            <UndoIcon />
          </UndoRedoButton>
        </CustomTooltip>
        <CustomTooltip
          title={tooltipWithShortcut(intl.formatMessage({ id: 'strings.redo' }), redoShortcut)}
          enterDelay={500}
        >
          <UndoRedoButton onClick={onRedo} disabled={redoDisabled}>
            <RedoIcon />
          </UndoRedoButton>
        </CustomTooltip>
      </UndoRedoContainer>
    </Toolbar>
  )
}

const TooltipLine = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const ShortcutLabel = styled.span`
  font-family: ui-monospace, monospace;
  color: #8a8a8e;
`

const Toolbar = styled.div`
  border-radius: 12px;
  border-top: 0.5px solid rgba(255, 255, 255, 0.15);
  border-bottom: 0.5px solid rgba(255, 255, 255, 0.15);
  background: rgba(20, 24, 26, 0.7);
  backdrop-filter: blur(5px);
  color: #fff;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 12px;
`

const ToggleToolButton = styled(IconButton)<{ $active: boolean }>`
  display: flex;
  color: white;
  width: 46px;
  height: 46px;
  padding: 8px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${(props) => (props.$active ? '#0bb163' : 'transparent')};
  transition: all 0.15s;
  cursor: pointer;
  border: 0;

  &:hover {
    background: ${(props) => (props.$active ? '#0bb163' : 'rgba(255, 255, 255, 0.1)')};
  }

  &:disabled {
    color: rgba(255, 255, 255, 0.5);
  }
`

const UndoRedoContainer = styled.div`
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  padding: 4px;
  justify-content: center;
  align-items: center;
  gap: 4px;
`

const UndoRedoButton = styled(IconButton)`
  display: flex;
  color: white;
  width: 36px;
  height: 36px;
  padding: 8px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.15s;
  cursor: pointer;
  border: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    color: rgba(255, 255, 255, 0.5);
  }
`
